/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';

// ── Colours ───────────────────────────────────────────────────────────
const C = {
  navy: '#445366', charcoal: '#2F3A42', mid: '#7A8A96', border: '#D8DEE3',
  cream: '#F7F6EF', white: '#FFFFFF', skyBlue: '#BCD9E5', skyText: '#3A8FA3',
  skyBlueLight: '#EAF4F8', green: '#7EA594', greenLight: '#EAF2EF',
  amber: '#E07B00', amberLight: '#FFF3E0', red: '#DC3545', redLight: '#FFF0F0',
  orange: '#E4AC49',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Helpers ───────────────────────────────────────────────────────────
function BalanceCard({ label, value, type, subtitle }) {
  if (value === null || value === undefined) return null;
  const n = parseFloat(value);
  const bg = n > 0 ? C.skyBlueLight : n < 0 ? C.redLight : C.cream;

  return (
    <div style={{
      background: bg, borderRadius: '16px', padding: '16px 20px',
      border: `1px solid ${C.border}`,
    }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
        {label}
      </div>
      <div style={{ fontSize: '32px', fontWeight: 800, color: C.charcoal, lineHeight: 1, marginBottom: '6px', fontVariantNumeric: 'tabular-nums' }}>
        {n}<span style={{ fontSize: '16px', fontWeight: 600, marginLeft: '4px' }}>h</span>
      </div>
      {subtitle && <div style={{ fontSize: '13px', color: C.mid, marginTop: '6px' }}>{subtitle}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.charcoal, borderRadius: '10px', padding: '12px 16px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)', fontSize: '13px',
    }}>
      <div style={{ color: C.skyBlue, fontWeight: 700, marginBottom: '8px' }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: C.white, display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.color, display: 'inline-block', flexShrink: 0 }} />
          <span style={{ color: '#aaa', fontSize: '12px' }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{p.value > 0 && p.name === 'OT' ? `+${p.value}` : p.value}h</span>
        </div>
      ))}
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────
function StatusBadge({ value, type }) {
  if (value === null || value === undefined) return null;
  const n = parseFloat(value);
  let label = '', bg = C.cream, color = C.mid;

  if (type === 'ot') {
    if (n > 8)      { label = 'High OT — worth a conversation';    bg = C.redLight;   color = C.red;   }
    else if (n > 0) { label = 'Some overtime accumulated';          bg = C.amberLight; color = C.amber; }
    else            { label = 'OT balance looks healthy';           bg = C.greenLight; color = C.green; }
  }
  if (type === 'leave') {
    if (n < 8)       { label = 'Low leave — try to plan time off'; bg = C.redLight;   color = C.red;   }
    else if (n < 16) { label = 'Leave balance is modest';           bg = C.amberLight; color = C.amber; }
    else             { label = 'Leave balance is healthy';          bg = C.greenLight; color = C.green; }
  }

  return (
    <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '20px', background: bg, color }}>
      {label}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN BALANCE PAGE
// ══════════════════════════════════════════════════════════════════════
export default function BalancePage() {
  const token = window.location.pathname.split('/').pop();
  const [state, setState] = useState('loading'); // loading | found | notfound | error
  const [confirmation, setConfirmation] = useState(null);
  const [member,       setMember]       = useState(null);
  const [chartData,    setChartData]    = useState([]);
  const [latestMonth,  setLatestMonth]  = useState('');
  const [windowOffset, setWindowOffset] = useState(0); // 0 = most recent 12 months

  useEffect(() => {
    async function load() {
      if (!token) { setState('notfound'); return; }

      // 1. Fetch the confirmation record
      const { data: conf, error: confErr } = await supabase
        .from('leave_confirmations')
        .select('*')
        .eq('token', token)
        .maybeSingle();

      if (confErr || !conf) { setState('notfound'); return; }
      setConfirmation(conf);

      // 2. Stamp viewed_at if first visit
      if (!conf.viewed_at) {
        await supabase
          .from('leave_confirmations')
          .update({ viewed_at: new Date().toISOString() })
          .eq('token', token);
      }

      // 3. Fetch team member (name, dept, venue only — no sensitive data)
      const { data: tm } = await supabase
        .from('team_members')
        .select('id, name, dept, venue')
        .eq('id', conf.team_member_id)
        .maybeSingle();

      if (!tm) { setState('error'); return; }
      setMember(tm);

      // 4. Fetch all balance history for this member
      const { data: allEntries } = await supabase
        .from('leave_ot_entries')
        .select('*, leave_ot_snapshots(month, year)')
        .eq('team_member_id', conf.team_member_id)
        .order('leave_ot_snapshots(year)', { ascending: true })
        .order('leave_ot_snapshots(month)', { ascending: true });

      if (allEntries) {
        const cd = allEntries
          .filter(e => e.leave_ot_snapshots)
          .map(e => {
            const notes = e.notes || '';
            const get = key => {
              const m = notes.match(new RegExp(`${key}=([^;]+)`));
              return m ? parseFloat(m[1]) || 0 : 0;
            };
            return {
              label:    `${MONTHS[e.leave_ot_snapshots.month - 1]} ${String(e.leave_ot_snapshots.year).slice(2)}`,
              ot:       parseFloat(e.ot_balance)    ?? null,
              leave:    parseFloat(e.leave_balance) ?? null,
              month:    e.leave_ot_snapshots.month,
              year:     e.leave_ot_snapshots.year,
              worked:   get('worked'),
              leaveHrs: get('leave'),
              sick:     get('sick'),
              special:  get('special'),
              unpaid:   get('unpaid'),
              contract: get('contract'),
            };
          })
          .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);

        setChartData(cd);
        if (cd.length > 0) {
          const last = cd[cd.length - 1];
          setLatestMonth(`${MONTHS[last.month - 1]} ${last.year}`);
        }
      }

      setState('found');
    }

    load();
  }, [token]);

  // ── Loading ───────────────────────────────────────────────────────
  if (state === 'loading') return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: C.mid }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
        <div style={{ fontSize: '14px' }}>Loading your balance…</div>
      </div>
    </div>
  );

  // ── Not found ─────────────────────────────────────────────────────
  if (state === 'notfound' || state === 'error') return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ background: C.white, borderRadius: '20px', padding: '48px 40px', maxWidth: '440px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h1 style={{ fontSize: '20px', fontWeight: 700, color: C.charcoal, margin: '0 0 10px' }}>Link not found</h1>
        <p style={{ fontSize: '14px', color: C.mid, lineHeight: 1.6, margin: 0 }}>
          This link may have expired or is invalid. Ask your manager to send a new one.
        </p>
      </div>
    </div>
  );

  // ── Found ─────────────────────────────────────────────────────────
  // Exclude current month from balance cards and line chart
  const now = new Date();
  const nowY = now.getFullYear();
  const nowM = now.getMonth() + 1;
  const completedData = chartData.filter(e => !(e.year === nowY && e.month === nowM));

  // 12-month window
  const WINDOW = 12;
  const totalMonths = completedData.length;
  const maxOffset   = Math.max(0, totalMonths - WINDOW);
  const clampedOffset = Math.min(windowOffset, maxOffset);
  const windowStart = Math.max(0, totalMonths - WINDOW - clampedOffset);
  const windowEnd   = Math.max(0, totalMonths - clampedOffset);
  const windowedData = completedData.slice(windowStart, windowEnd);

  const latest    = completedData[completedData.length - 1];
  const prevEntry = completedData[completedData.length - 2];

  const otDiff    = latest && prevEntry && latest.ot    !== null && prevEntry.ot    !== null ? (latest.ot    - prevEntry.ot).toFixed(1)    : null;
  const leaveDiff = latest && prevEntry && latest.leave !== null && prevEntry.leave !== null ? (latest.leave - prevEntry.leave).toFixed(1) : null;

  const deptLabel = member?.dept === 'FOH' ? '☕ FOH' : member?.dept === 'KIT' ? '🍳 Kitchen' : member?.dept || '';
  const venueLabel = member?.venue?.replace('Box ', '') || '';

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Header */}
      <div style={{ background: C.navy, padding: '0' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '28px 24px 32px' }}>
          {/* Logo / brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
            <div style={{ width: 36, height: 36, borderRadius: '8px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
              🍋
            </div>
            <span style={{ fontSize: '14px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.05em' }}>BOX SOCIAAL</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h1 style={{ margin: '0 0 6px', fontSize: '28px', fontWeight: 800, color: C.white, letterSpacing: '-0.5px' }}>
                Hey, {member?.name?.split(' ')[0]} 👋
              </h1>
              <div style={{ fontSize: '14px', color: C.skyBlue }}>
                {deptLabel} · {venueLabel}
              </div>
            </div>
            {latestMonth && (
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 16px', textAlign: 'right' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: C.skyBlue, textTransform: 'uppercase', letterSpacing: '0.06em' }}>As of</div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: C.white }}>{latestMonth}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <div style={{ background: C.navy, marginBottom: '-1px' }}>
        <svg viewBox="0 0 1440 40" style={{ display: 'block', width: '100%' }} preserveAspectRatio="none">
          <path d="M0,40 C360,0 1080,40 1440,10 L1440,40 Z" fill={C.cream} />
        </svg>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '32px 24px 60px' }}>

        {/* Intro */}
        <div style={{ background: C.white, borderRadius: '16px', padding: '20px 24px', marginBottom: '24px', border: `1px solid ${C.border}` }}>
          <p style={{ margin: 0, fontSize: '14px', color: C.mid, lineHeight: 1.7 }}>
            Here's an overview of your current <strong style={{ color: C.charcoal }}>overtime</strong> and <strong style={{ color: C.charcoal }}>leave balances</strong>. 
            These are updated monthly. If anything looks off, speak to your manager.
          </p>
        </div>

        {/* Balance cards */}
        {latest && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            <BalanceCard
              label="Overtime Balance"
              value={latest.ot}
              subtitle={otDiff !== null ? `${parseFloat(otDiff) > 0 ? '↑ +' : '↓ '}${otDiff}h vs ${prevEntry?.label || 'last month'}` : undefined}
            />
            <BalanceCard
              label="Leave Balance"
              value={latest.leave}
              subtitle={leaveDiff !== null ? `${parseFloat(leaveDiff) > 0 ? '↑ +' : '↓ '}${leaveDiff}h vs ${prevEntry?.label || 'last month'}` : undefined}
            />
            <BalanceCard
              label="Combined"
              value={latest.ot !== null && latest.leave !== null ? Math.round((latest.ot + latest.leave) * 10) / 10 : null}
            />
          </div>
        )}

        {/* Chart */}
        {completedData.length > 1 && (
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', marginBottom: '24px', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: C.charcoal }}>Balance History</div>
                <div style={{ fontSize: '12px', color: C.mid, marginTop: '2px' }}>{totalMonths} months of data</div>
              </div>
              {totalMonths > WINDOW && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button onClick={() => setWindowOffset(o => Math.min(o + WINDOW, maxOffset))} disabled={clampedOffset >= maxOffset}
                    style={{ padding: '3px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, background: clampedOffset >= maxOffset ? C.cream : C.white, color: clampedOffset >= maxOffset ? C.border : C.charcoal, cursor: clampedOffset >= maxOffset ? 'default' : 'pointer', fontSize: '13px', fontWeight: 700 }}>‹</button>
                  <span style={{ fontSize: '11px', color: C.mid, minWidth: '60px', textAlign: 'center' }}>{windowStart + 1}–{windowEnd} of {totalMonths}</span>
                  <button onClick={() => setWindowOffset(o => Math.max(o - WINDOW, 0))} disabled={clampedOffset === 0}
                    style={{ padding: '3px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, background: clampedOffset === 0 ? C.cream : C.white, color: clampedOffset === 0 ? C.border : C.charcoal, cursor: clampedOffset === 0 ? 'default' : 'pointer', fontSize: '13px', fontWeight: 700 }}>›</button>
                </div>
              )}
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={windowedData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.mid }} />
                <YAxis tick={{ fontSize: 11, fill: C.mid }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                <ReferenceLine y={0} stroke={C.red} strokeDasharray="4 4" strokeOpacity={0.4} />
                <Line type="monotone" dataKey="ot"    name="OT"    stroke={C.orange}  strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="leave" name="Leave" stroke={C.skyText} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Hours breakdown stacked bar chart — windowed, full history */}
        {chartData.length > 0 && (() => {
          const barData = windowedData;
          const contractHrs = chartData.length ? (Math.max(...chartData.map(d => d.contract)) || null) : null;
          return (
          <div style={{ background: C.white, borderRadius: '16px', padding: '24px', marginBottom: '24px', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: C.charcoal, marginBottom: '4px' }}>Hours Breakdown</div>
            <div style={{ fontSize: '12px', color: C.mid, marginBottom: '16px' }}>
              Showing {windowStart + 1}–{windowEnd} of {totalMonths} months — dotted line is your contracted hours
            </div>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {[
                { color: '#4A7B8C', label: 'Worked'       },
                { color: '#A8D5B5', label: 'Leave'        },
                { color: '#E07B00', label: 'Sick'         },
                { color: '#F4B8C1', label: 'Special Leave'},
                { color: '#8C8C8C', label: 'No-pay Leave' },
              ].map(({ color, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: C.mid }}>
                  <span style={{ width: 10, height: 10, borderRadius: '2px', background: color, display: 'inline-block' }}/>
                  {label}
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.mid }} />
                <YAxis tick={{ fontSize: 11, fill: C.mid }} tickFormatter={v => `${v}h`} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const total    = payload.reduce((s, p) => s + (p.value || 0), 0);
                    const contract = payload[0]?.payload?.contract ?? null;
                    return (
                      <div style={{ background: '#2F3A42', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', minWidth: '160px' }}>
                        <div style={{ color: '#BCD9E5', fontWeight: 700, marginBottom: '8px' }}>{label}</div>
                        {payload.map(p => p.value > 0 && (
                          <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ width: 8, height: 8, borderRadius: '2px', background: p.fill, display: 'inline-block' }}/>
                              <span style={{ color: '#bbb' }}>{p.name}</span>
                            </div>
                            <span style={{ color: '#fff', fontWeight: 600 }}>{p.value}h</span>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#bbb' }}>Total</span>
                          <span style={{ color: '#fff', fontWeight: 700 }}>{Math.round(total * 10) / 10}h</span>
                        </div>
                        {contract > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
                            <span style={{ color: '#bbb' }}>OT this month</span>
                            <span style={{ fontWeight: 700, color: '#fff' }}>
                              {Math.round((total - contract) * 10) / 10 >= 0 ? '+' : ''}{Math.round((total - contract) * 10) / 10}h
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                {contractHrs && (
                  <ReferenceLine y={contractHrs} stroke={C.amber} strokeDasharray="5 3" strokeWidth={1.5} />
                )}
                <Bar dataKey="worked"   name="Worked"        stackId="a" fill="#4A7B8C" radius={[0,0,0,0]} />
                <Bar dataKey="leaveHrs" name="Leave"         stackId="a" fill="#A8D5B5" radius={[0,0,0,0]} />
                <Bar dataKey="sick"     name="Sick"          stackId="a" fill="#E07B00" radius={[0,0,0,0]} />
                <Bar dataKey="special"  name="Special Leave" stackId="a" fill="#F4B8C1" radius={[0,0,0,0]} />
                <Bar dataKey="unpaid"   name="No-pay Leave"  stackId="a" fill="#8C8C8C" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          );
        })()}

        {/* Monthly breakdown table */}
        {chartData.length > 0 && (
          <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: '24px' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: C.charcoal }}>Month by Month</div>
            </div>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '320px' }}>
              <thead>
                <tr style={{ background: C.cream }}>
                  {['Month', 'OT Balance', 'Leave Balance'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Month' ? 'left' : 'right', fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...chartData].reverse().map((row, i) => {
                  const isLatest  = i === 0;
                  const prevRow   = [...chartData].reverse()[i + 1];
                  const otMove    = prevRow ? Math.round((row.ot - prevRow.ot) * 10) / 10 : null;
                  const leaveMove = prevRow ? Math.round((row.leave - prevRow.leave) * 10) / 10 : null;
                  const movStyle  = v => ({ fontSize: '12px', fontWeight: 600, color: v > 0 ? C.amber : v < 0 ? C.green : C.mid });
                  return (
                    <tr key={row.label} style={{ borderBottom: `1px solid ${C.border}`, background: isLatest ? C.skyBlueLight : i % 2 === 0 ? C.white : C.cream }}>
                      <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: isLatest ? 700 : 400, color: C.charcoal, whiteSpace: 'nowrap' }}>
                        {row.label}
                        {isLatest && <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 700, background: C.skyBlue, color: C.white, padding: '1px 6px', borderRadius: '20px' }}>LATEST</span>}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: row.ot < 0 ? C.red : C.charcoal, whiteSpace: 'nowrap' }}>
                        {row.ot !== null ? `${row.ot}h` : '—'}
                        {otMove !== null && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: otMove > 0 ? C.amber : otMove < 0 ? C.green : C.mid }}>{otMove > 0 ? `+${otMove}` : otMove}h</span>}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: row.leave < 0 ? C.red : C.charcoal, whiteSpace: 'nowrap' }}>
                        {row.leave !== null ? `${row.leave}h` : '—'}
                        {leaveMove !== null && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: leaveMove > 0 ? C.amber : leaveMove < 0 ? C.green : C.mid }}>{leaveMove > 0 ? `+${leaveMove}` : leaveMove}h</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {/* CSV Export */}
        {member && (
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <button
              onClick={async () => {
                const eitjeId = confirmation?.team_member_id ? null : null; // fetched via member
                // Fetch hours via member id — eitje_user_id lookup
                const { data: tm } = await supabase.from('team_members').select('eitje_user_id, balance_calc_start_date, join_date').eq('id', member.id).maybeSingle();
                if (!tm?.eitje_user_id) { alert('No Eitje ID on file.'); return; }
                const startDate = tm.balance_calc_start_date || tm.join_date;
                if (!startDate) { alert('No start date on file.'); return; }
                const { data, error } = await supabase.from('eitje_hours')
                  .select('date, type, hours_worked')
                  .eq('eitje_user_id', parseInt(tm.eitje_user_id, 10))
                  .in('type', ['gewerkte_uren','verlof','ziek','bijzonder_verlof'])
                  .gte('date', startDate)
                  .order('date');
                if (error || !data?.length) { alert('No hours data found.'); return; }
                const TYPE_LABELS = { gewerkte_uren: 'Worked', verlof: 'Leave', ziek: 'Sick', bijzonder_verlof: 'Special Leave' };
                const rows = [['Date','Type','Hours'], ...data.map(r => [r.date, TYPE_LABELS[r.type] || r.type, parseFloat(r.hours_worked).toFixed(2)])];
                const csv  = rows.map(r => r.join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement('a');
                a.href = url; a.download = `${member.name.replace(/\s+/g,'_')}_hours.csv`; a.click();
                URL.revokeObjectURL(url);
              }}
              style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              ↓ Download My Hours (CSV)
            </button>
          </div>
        )}

        {/* Footer note */}
        <div style={{ textAlign: 'center', fontSize: '12px', color: C.mid, lineHeight: 1.6 }}>
          This page was sent to you by your manager · Box Sociaal Group<br />
          Questions about your balance? Speak to your venue manager.
        </div>

      </div>
    </div>
  );
}